import axios from "axios";
import { EditorState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import config from "../../utils/config";
import { Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import { emailTemplateTypeOptions } from "../../utils/email-template-types";
import parse from "html-react-parser";

const info = {
  1: ["Use %PASSWORD% to display the user's password."],
  2: [
    "Use %ESNCARD% to display the ESNcard",
    "Use %DISCOUNT% to display information about events with special discount enabled.",
  ],
  4: [
    "Use %EVENT% to display the name of the event.",
    "Use %CUSTOM% to display the content of the custom section.",
    "Use %EXTERNAL% to display the external payment information.",
    "Use %LINK% to display the link for paying online.",
  ],
  5: [
    "Use %EVENT% to display the name of the event.",
    "Use %CUSTOM% to display the content of the custom section.",
  ],
  7: [
    "Use %EVENT% to display the event name.",
    "Use %LINK% to display the link for paying online.",
  ],
  8: ["Use %EVENT% to display the event name."],
};

function EmailTemplates(props) {
  const [emailType, setEmailType] = useState("");
  const [subject, setSubject] = useState("Default Subject");
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(stateFromHTML("<p>Default Content</p>")),
  );

  useEffect(() => {
    setEmailType("");
    setSubject("Default Subject");
    setEditorState(EditorState.createWithContent(stateFromHTML("<p>Default Content</p>")));
  }, [props.organization]); // eslint-disable-line

  useEffect(() => {
    fetchEmailTemplate();
  }, [emailType]); // eslint-disable-line

  const update = useDebouncedCallback(
    () => {
      let content = stateToHTML(editorState.getCurrentContent());

      if (subject === "" || content === "") {
        toast.warning("The subject and content of the email cannot be empty");
        return;
      }

      axios
        .post(
          `${config.API_ENDPOINT}/email-settings/template/${props.organization}/${emailType}`,
          { subject, content },
          { headers: { "x-access-token": localStorage.getItem("access-token") } },
        )
        .then(() => toast.success("Success!"))
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true },
  );

  const fetchEmailTemplate = () => {
    if (emailType === "") return;

    axios
      .get(`${config.API_ENDPOINT}/email-settings/template/${props.organization}/${emailType}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        setSubject(res.data.subject);
        setEditorState(EditorState.createWithContent(stateFromHTML(res.data.content)));
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 404:
            setSubject("Default Subject");
            setEditorState(EditorState.createWithContent(stateFromHTML("<p>Default Content</p>")));
            break;
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  return (
    <Card sx={{ p: 1.5 }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Template Editor</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}>
          Select an email type to customize it.
        </Typography>

        <FormControl sx={{ mb: 2 }} fullWidth>
          <InputLabel id="emailtype-label">Email Type</InputLabel>
          <Select
            label="Email Type"
            labelId="emailtype-label"
            value={emailType}
            onChange={(e) => setEmailType(e.target.value)}
          >
            {emailTemplateTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {emailType ? (
          <>
            <TextField
              variant="outlined"
              required
              fullWidth
              label="Subject"
              name="subject"
              autoFocus
              type="text"
              autoComplete="off"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
            />
            {info[emailType] ? (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Variables</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {info[emailType].map((val) => (
                    <Chip key={val} label={val} variant="outlined" />
                  ))}
                </Box>
              </Box>
            ) : null}
            <Card sx={{ p: 0.5 }}>
              <CardContent sx={{ padding: 5 }}>
                <Editor
                  editorStyle={{ minHeight: 200 }}
                  editorState={editorState}
                  label="Content..."
                  onEditorStateChange={(s) => setEditorState(s)}
                />
              </CardContent>
            </Card>
            <Typography sx={{ mt: 2.5, mb: 2, fontWeight: 700 }}>
              Preview
            </Typography>
            <Typography>Subject: {subject}</Typography>
            {parse(stateToHTML(editorState.getCurrentContent()))}
            <Button
              sx={{ mt: 4, color: "white", textTransform: "none", fontWeight: 600 }}
              variant="contained"
              color="primary"
              onClick={update}
            >
              Update
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default EmailTemplates;